import { Body, Button, Card, MediumSpin } from "@gadgetinc/widgets";
import { useStyletron } from "baseui";
import { toaster } from "baseui/toast";
import { HeadingXLarge } from "baseui/typography";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Virtuoso } from "react-virtuoso";
import useFetch from "use-http";
import { useDebouncedValue } from "web/src/lib/hooks";
import type { DocsInterfaceProps } from "../../../src/chrome/DocsInterface";
import { DocsInterface } from "../../../src/chrome/DocsInterface";
import ChangelogItem, { type Changelog } from "./components/ChangelogItem";
import FilterMenu from "./components/FilterMenu";

const query = `
  mutation ($filter: JSONObject, $search: String, $after: String) {
    getChangelogs(filter: $filter, search: $search, after: $after) {
      success
      errors {
        message
      }
      result
    }
  }
`;

export default function (props: DocsInterfaceProps) {
  const [css, $theme] = useStyletron();
  const [changelogs, setChangelogs] = useState<Changelog[]>([]);
  const [selectedRepositories, setSelectedRepositories] = useState<string[]>([]);
  const [search, setSearch] = useState<string>("");
  const [after, setAfter] = useState<string>("");
  const processedResponseRef = useRef<string>("");

  /**
   * Make sure to unpause your development environment to see the changelogs
   * Example: https://gadget-changelog--devaoc.gadget.app/edit/staff
   * Change the environment target "--devaoc" to your env
   */
  const [{ data: response, loading, error, post }] = useFetch(
    `https://gadget-changelog${process.env.NODE_ENV === "development" ? "--devaoc" : ""}.gadget.app/api/graphql`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
    }
  );

  const handleRepositoryToggle = useCallback((repositoryValue: string) => {
    setSelectedRepositories((prev) =>
      prev.includes(repositoryValue) ? prev.filter((repo) => repo !== repositoryValue) : [...prev, repositoryValue]
    );
  }, []);

  useEffect(() => {
    // Reset changelogs when filter changes
    setChangelogs([]);
    setAfter("");

    processedResponseRef.current = "";
  }, [selectedRepositories]);

  const emptyState = useDebouncedValue(selectedRepositories.length && !loading && !changelogs.length, 100);

  useEffect(() => {
    void post({
      query,
      variables: {
        filter: {
          pullRequest: {
            repository: {
              in: selectedRepositories,
            },
          },
        },
        search,
        after,
      },
    });
  }, [selectedRepositories, search, after, post]);

  useEffect(() => {
    if (response?.data?.getChangelogs?.success && response?.data?.getChangelogs?.result) {
      const newChangelogs = response.data.getChangelogs.result.changelogs;
      const responseKey = `${after}-${JSON.stringify(selectedRepositories)}-${search}`;

      // Check if we've already processed this exact response
      if (processedResponseRef.current === responseKey) return;

      processedResponseRef.current = responseKey;

      setChangelogs((prevChangelogs) => {
        // If this is the first load (no after cursor), replace the array
        if (!after) return newChangelogs;
        // Otherwise append to existing
        return [...prevChangelogs, ...newChangelogs];
      });
    }
  }, [response?.data?.getChangelogs?.success, response?.data?.getChangelogs?.result]);

  // Handle errors with toast notifications
  useEffect(() => {
    if (error) {
      toaster.negative(`Failed to load changelogs: ${error.message}`, { autoHideDuration: 5000 });
      console.error(error);
    } else if (response?.data?.getChangelogs?.errors?.length > 0) {
      const errorMessage = response.data.getChangelogs.errors[0]?.message || "Unknown error occurred";
      console.error(errorMessage);
      toaster.negative(`Changelog error: ${errorMessage}`, { autoHideDuration: 5000 });
    }
  }, [error, response?.data?.getChangelogs?.errors]);

  return (
    <DocsInterface {...props} showLeftSideNav={true}>
      <div
        className={css({
          display: "flex",
          alignItems: "flex-start",
          marginLeft: $theme.sizing.scale900,
          marginTop: $theme.sizing.scale900,
          marginRight: $theme.sizing.scale900,
          height: "calc(100vh - 125px)",
          gap: $theme.sizing.scale800,
        })}
      >
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: $theme.sizing.scale800,
            alignSelf: "stretch",
            width: "100%",
          })}
        >
          <div
            className={css({
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: $theme.sizing.scale300,
              width: "850px",
              "@media (max-width: 1485px)": {
                width: "100%",
              },
            })}
          >
            <HeadingXLarge>Changelog</HeadingXLarge>
            <Body
              $style={{
                color: $theme.colors.contentPrimary,
                fontFamily: $theme.typography.font100.fontFamily,
              }}
            >
              The latest changes and updates to Gadget
            </Body>
          </div>
          <Card
            $style={{
              display: "none",
              backgroundColor: $theme.colors.primary100,
              "@media (max-width: 1485px)": {
                display: "block",
                width: "100%",
              },
            }}
          >
            <FilterMenu {...{ handleRepositoryToggle, selectedRepositories }} />
          </Card>
          <div
            className={css({
              height: "100%",
              display: "flex",
              flexDirection: "column",
              flexGrow: 1,
              overflow: "hidden",
              width: "850px",
              "@media (max-width: 1485px)": {
                width: "100%",
              },
            })}
          >
            {emptyState ? (
              <div
                className={css({
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  padding: $theme.sizing.scale600,
                  color: $theme.colors.contentSecondary,
                  fontFamily: $theme.typography.font100.fontFamily,
                  border: `1px dashed ${$theme.colors.borderOpaque}`,
                  borderStyle: "dashed",
                  borderWidth: "1px",
                  borderColor: $theme.colors.borderOpaque,
                  borderDasharray: "8,4",
                  borderRadius: $theme.borders.radius200,
                  gap: $theme.sizing.scale400,
                })}
              >
                <Body>No results found for the selected repositories.</Body>
                <Button onClick={() => setSelectedRepositories([])}>Clear filters</Button>
              </div>
            ) : (
              <Virtuoso
                data={changelogs}
                totalCount={changelogs?.length || 0}
                itemContent={(index, changelog) => <ChangelogItem key={index} changelog={changelog} />}
                className={css({
                  height: "100%",
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  "::-webkit-scrollbar": {
                    display: "none",
                  },
                })}
                endReached={() => {
                  if (response?.data?.getChangelogs?.result?.hasNextPage) setAfter(response?.data?.getChangelogs?.result?.endCursor);
                }}
                increaseViewportBy={200}
                components={{
                  Footer: () =>
                    loading ? (
                      <div
                        className={css({
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          padding: $theme.sizing.scale600,
                          width: "100%",
                        })}
                      >
                        <MediumSpin />
                      </div>
                    ) : null,
                }}
              />
            )}
          </div>
        </div>
        <div
          className={css({
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            flexShrink: 0,
            width: "300px",
            "@media (max-width: 1485px)": {
              display: "none",
            },
          })}
        >
          <FilterMenu {...{ handleRepositoryToggle, selectedRepositories }} />
        </div>
      </div>
    </DocsInterface>
  );
}
